import React, { useContext, useEffect, useRef, useState } from 'react';
import { Graph } from 'react-d3-graph';
import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Tour,
  TourProps,
  Typography,
} from 'antd';

import {
  DEVICE_TYPE_CONFIG,
  getDeviceSymbolPath,
  getDeviceTypeConfig,
} from '@totem/components/portfolio/buildings/network/deviceTypeConfig';
import NetworkContext from '@totem/components/portfolio/buildings/network/NetworkContext';
import NetworkDeviceDetail from '@totem/components/portfolio/buildings/network/networkDeviceDetail';
import { NetworkDiagramNode } from '@totem/components/portfolio/buildings/network/types';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import { isNotNull } from '@totem/utilities/common';

import './network.css';

const { Text } = Typography;

const LEGEND_ENTRIES = Object.entries(DEVICE_TYPE_CONFIG).filter(
  ([key]) => key !== 'accesspoint',
);

const getStrokeColor = (onlineStatus: string): string => {
  switch (onlineStatus?.toLowerCase()) {
    case 'online':
      return '#52c41a';
    case 'offline':
      return '#ff4d4f';
    default:
      return '#d9d9d9';
  }
};

// Height of all chrome above the card body:
// breadcrumbs (~44px) + page title (~56px) + tab bar (~46px) + card header (~57px) + bottom margin (16px)
const CHROME_HEIGHT = 220;
// Height reserved for the disconnected-devices strip when it is visible
const DISCONNECTED_STRIP_HEIGHT = 72;

const NetworkGraph = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  const disconnectedRef = useRef<HTMLDivElement>(null);
  // null until the container has been measured — Graph won't render until we have a real width
  const [dimensions, setDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set());
  const [tourOpen, setTourOpen] = useState(false);
  const {
    data,
    isLoading,
    selectedNodeId,
    selectedNetworkId,
    setSelectedNodeId,
    setSelectedNetworkId,
  } = useContext(NetworkContext);

  // The IBCard + containerRef div are always rendered so this effect can always observe them.
  // Loading / empty states are shown *inside* the container rather than via early returns.
  useEffect(() => {
    const applyDimensions = () => {
      const width = containerRef.current?.clientWidth ?? 0;
      const height = Math.max(400, window.innerHeight - CHROME_HEIGHT);
      if (width > 0) {
        setDimensions({ width, height });
      }
    };

    // Debounce resizes so the Graph remounts only after the user stops dragging
    const scheduleDimensionUpdate = () => {
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
      }
      resizeTimerRef.current = setTimeout(applyDimensions, 200);
    };

    const observer = new ResizeObserver(scheduleDimensionUpdate);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    window.addEventListener('resize', scheduleDimensionUpdate);

    // Measure immediately — layout is settled by the time useEffect fires
    applyDimensions();

    return () => {
      observer.disconnect();

      window.removeEventListener('resize', scheduleDimensionUpdate);
      if (resizeTimerRef.current) {
        clearTimeout(resizeTimerRef.current);
      }
    };
  }, []);

  const toggleHiddenType = (type: string) => {
    setHiddenTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const graphHeight =
    dimensions?.height ?? Math.max(400, window.innerHeight - CHROME_HEIGHT);

  // Compute graph data — safe to call unconditionally; guarded by showGraph below
  const hasData = isNotNull(data) && (data.nodes?.length ?? 0) > 0;

  const visibleNodeIds: Set<string> | null =
    hasData && selectedNetworkId
      ? new Set(
          data.nodes
            .filter((node: NetworkDiagramNode) =>
              node.networkIds?.includes(selectedNetworkId),
            )
            .map((node: NetworkDiagramNode) => node.id),
        )
      : null;

  const graphNodes = hasData
    ? data.nodes
        .filter(
          (node: NetworkDiagramNode) =>
            (!visibleNodeIds || visibleNodeIds.has(node.id)) &&
            !hiddenTypes.has(node.deviceType?.toLowerCase() ?? 'unknown'),
        )
        .map((node: NetworkDiagramNode) => {
          const typeConfig = getDeviceTypeConfig(node.deviceType);
          return {
            id: node.id,
            label: node.label || node.id,
            color: typeConfig.color,
            symbolType: typeConfig.symbolType,
            strokeColor: getStrokeColor(node.onlineStatus),
            strokeWidth: 2,
            size: 400,
          };
        })
    : [];

  const renderedNodeIds = new Set(graphNodes.map((node) => node.id));

  const graphLinks = hasData
    ? data.edges
        .filter(
          (edge) =>
            renderedNodeIds.has(edge.source) &&
            renderedNodeIds.has(edge.target),
        )
        .map((edge) => ({ source: edge.source, target: edge.target }))
    : [];

  // Split nodes into connected (shown in force graph) and isolated (shown in strip below)
  const connectedNodeIds = new Set<string>();
  graphLinks.forEach((link) => {
    connectedNodeIds.add(link.source as string);

    connectedNodeIds.add(link.target as string);
  });

  const connectedGraphNodes = graphNodes.filter((node) =>
    connectedNodeIds.has(node.id),
  );
  const isolatedGraphNodes = graphNodes.filter(
    (node) => !connectedNodeIds.has(node.id),
  );

  const hasIsolated = isolatedGraphNodes.length > 0;
  const effectiveGraphHeight = hasIsolated
    ? graphHeight - DISCONNECTED_STRIP_HEIGHT
    : graphHeight;

  const graphConfig = {
    directed: false,
    width: dimensions?.width ?? 800,
    height: effectiveGraphHeight,
    nodeHighlightBehavior: true,
    node: {
      labelProperty: 'label',
      fontSize: 11,
      highlightFontSize: 13,
      highlightStrokeColor: '#40a9ff',
      highlightStrokeWidth: 4,
    },
    link: {
      highlightColor: '#40a9ff',
      color: '#d9d9d9',
    },
    d3: {
      gravity: -250,
      linkLength: 150,
    },
  };

  const networkOptions = [
    { label: 'No Filter', value: '' },
    ...(hasData
      ? (data.networks ?? []).map((net) => ({ label: net.name, value: net.id }))
      : []),
  ];

  const selectedNode =
    hasData && selectedNodeId
      ? (data.nodes.find(
          (node: NetworkDiagramNode) => node.id === selectedNodeId,
        ) ?? null)
      : null;

  const tourSteps: TourProps['steps'] = [
    {
      title: 'Network Diagram',
      description:
        "This diagram shows your building's devices and how they connect to each other. " +
        'Drag any node to reposition it. Click a node to open its detail panel.',
      target: () => containerRef.current,
      placement: 'center',
    },
    {
      title: 'Filter by Network',
      description:
        'Narrow the diagram to show only devices on a specific network segment. ' +
        'Choose "No Filter" to return to the full view.',
      target: () => filterRef.current,
      placement: 'bottom',
    },
    {
      title: 'Device Type Legend',
      description:
        'Each shape and color represents a device type. ' +
        'Click any entry to hide or show that device type in the diagram. ' +
        'The border color of each node indicates online (green) or offline (red) status.',
      target: () => legendRef.current,
      placement: 'bottomRight',
    },
    ...(hasIsolated
      ? [
          {
            title: 'Disconnected Devices',
            description:
              'Devices listed here have no connections to any other device in the current view. ' +
              'Click a device tag to open its detail panel.',
            target: () => disconnectedRef.current,
            placement: 'top' as const,
          },
        ]
      : []),
  ];

  const toolbar = (
    <div styleName="toolbar">
      <div
        ref={filterRef}
        style={{ display: 'inline-flex', alignItems: 'center' }}
      >
        <Space wrap>
          <Text>Filter by Network:</Text>
          <Select
            style={{ width: 220 }}
            options={networkOptions}
            value={selectedNetworkId ?? ''}
            onChange={(val) => setSelectedNetworkId(val || null)}
          />
        </Space>
      </div>
      <div ref={legendRef} styleName="legend">
        {LEGEND_ENTRIES.map(([type, cfg]) => {
          const isHidden = hiddenTypes.has(type);
          return (
            <span
              key={type}
              styleName="legend-item"
              title={isHidden ? `Show ${type}` : `Hide ${type}`}
              style={{
                cursor: 'pointer',
                opacity: isHidden ? 0.35 : 1,
                textDecoration: isHidden ? 'line-through' : 'none',
              }}
              onClick={() => toggleHiddenType(type)}
            >
              <svg
                width="14"
                height="14"
                viewBox="-8 -8 16 16"
                styleName="legend-symbol"
              >
                <path
                  d={getDeviceSymbolPath(cfg.symbolType)}
                  fill={cfg.color}
                />
              </svg>
              <span styleName="legend-label">{type}</span>
            </span>
          );
        })}
        <span styleName="legend-divider" />
        <span styleName="legend-item">
          <span
            styleName="legend-dot"
            style={{ background: '#52c41a', borderRadius: 0 }}
          />
          <span styleName="legend-label">online</span>
        </span>
        <span styleName="legend-item">
          <span
            styleName="legend-dot"
            style={{ background: '#ff4d4f', borderRadius: 0 }}
          />
          <span styleName="legend-label">offline</span>
        </span>
      </div>
      <Tooltip title="Help / Tour">
        <Button
          type="text"
          icon={<QuestionCircleOutlined />}
          onClick={() => setTourOpen(true)}
        />
      </Tooltip>
    </div>
  );

  // Only render the graph once we have real dimensions and real data
  const showGraph = dimensions !== null && !isLoading && hasData;
  const graphKey = `${dimensions?.width}x${effectiveGraphHeight}-${selectedNetworkId ?? 'all'}-${[...hiddenTypes].sort().join(',')}`;

  return (
    <IBCard
      title="Network"
      extra={toolbar}
      styles={{
        body: {
          padding: 0,
          height: graphHeight,
          overflow: 'hidden',
        },
      }}
    >
      <div styleName="graph-root">
        <div ref={containerRef} styleName="graph-container">
          {isLoading && (
            <div styleName="centered">
              <Spin size="large" />
            </div>
          )}
          {!isLoading && !hasData && (
            <div styleName="centered">No network data available.</div>
          )}
          {showGraph && (
            <Graph
              key={graphKey}
              id="building-network-graph"
              data={{ nodes: connectedGraphNodes, links: graphLinks }}
              config={graphConfig}
              onClickNode={(nodeId: string) =>
                setSelectedNodeId(nodeId === selectedNodeId ? null : nodeId)
              }
              onClickGraph={() => setSelectedNodeId(null)}
            />
          )}
        </div>

        {hasIsolated && (
          <div
            ref={disconnectedRef}
            style={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              padding: '8px 12px',
              borderTop: '1px solid #f0f0f0',
              height: DISCONNECTED_STRIP_HEIGHT,
              overflowY: 'auto',
            }}
          >
            <Text
              type="secondary"
              style={{
                flexShrink: 0,
                fontSize: 12,
                lineHeight: '24px',
                whiteSpace: 'nowrap',
              }}
            >
              Disconnected ({isolatedGraphNodes.length})
            </Text>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 4,
                alignContent: 'flex-start',
              }}
            >
              {isolatedGraphNodes.map((node) => {
                const typeConfig = getDeviceTypeConfig(
                  // eslint-disable-next-line id-length
                  data.nodes.find((n: NetworkDiagramNode) => n.id === node.id)
                    ?.deviceType ?? 'unknown',
                );
                return (
                  <Tooltip key={node.id} title={node.label}>
                    <Tag
                      color={typeConfig.color}
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        setSelectedNodeId(
                          node.id === selectedNodeId ? null : node.id,
                        )
                      }
                    >
                      {node.label}
                    </Tag>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        )}

        {selectedNode && (
          <NetworkDeviceDetail
            node={selectedNode}
            networks={data.networks ?? []}
            onClose={() => setSelectedNodeId(null)}
          />
        )}
      </div>
      <Tour
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        steps={tourSteps}
      />
    </IBCard>
  );
};

export default NetworkGraph;
